
import * as React from 'react';
import strings from '../cache/emptyFolder/EmptyFolder.resx';
import { connectToItemCache } from '../decorators/connectToItemCache';
import { IItemCache } from '../../models/IItemCacheObserver';
import { emptyFolderControl } from '../../controls/emptyFolderControl';
import { WithItemCache, IItemCacheStateProps } from '../withItemCache/WithItemCache';
import { childItemsFacet } from '../../facets/SetFacets';
import { IStyleFunction } from 'office-ui-fabric-react/lib/Utilities';
import {
  IEmptyFolderStyleProps,
  IEmptyFolderStyles,
  getStyles as getDefaulStyles
} from './EmptyFolder.styles';
import { classNamesFunction, styled } from 'office-ui-fabric-react/lib/Utilities';
import { Omit } from '../../utilities/Omit';
import { getTheme, ITheme } from 'office-ui-fabric-react/lib/Styling';

/**
 * @internal
 */
export interface IEmptyFolderBaseProps {
  itemKey: string;
  childItems: typeof childItemsFacet.type | undefined;
  theme?: ITheme;
  styles?: IStyleFunction<IEmptyFolderStyleProps, IEmptyFolderStyles>;
  onLayoutRenderComplete(): void;
}

const getClassNames = classNamesFunction<IEmptyFolderStyleProps, IEmptyFolderStyles>();

/* tslint:disable-next-line:max-line-length */
const EMPTY_FOLDER_ICON_URL = 'https://static2.sharepointonline.com/files/fabric/office-ui-fabric-react-assets/images/emptyfolder/empty_folder.svg';

class EmptyFolderComponent extends React.PureComponent<{
  emptyFolderIconUrl: string | undefined;
  emptyFolderTitle: string | undefined;
} & Omit<IEmptyFolderBaseProps, 'itemKey' | 'childItems'>> {
  public render(): JSX.Element {
    const {
      emptyFolderIconUrl,
      emptyFolderTitle,
      theme,
      styles
    } = this.props;

    const classNames: { [key in keyof IEmptyFolderStyles]: string } = getClassNames(styles, {
      theme: theme || getTheme()
    });

    return (
      <div className={ classNames.root } data-automationid='emptyFolderContainer'>
        <div
          className={ classNames.emptyFolderImageContainer }
        >
          <img
            className={ classNames.emptyFolderImage }
            src={ emptyFolderIconUrl || EMPTY_FOLDER_ICON_URL }
            alt={ strings.emptyFolderDefaultAltText }
          />
        </div>
        <div className={ classNames.emptyFolderTitle }>
          { emptyFolderTitle || strings.emptyFolderTitle }
        </div>
      </div>);
  }

  public componentDidMount(): void {
    this.props.onLayoutRenderComplete();
  }
}

const EmptyFolderComponentStyled: (props: {
  emptyFolderIconUrl: string | undefined;
  emptyFolderTitle: string | undefined;
} & Omit<IEmptyFolderBaseProps, 'itemKey' | 'childItems'>) => JSX.Element = styled(
  EmptyFolderComponent,
  getDefaulStyles
);

/**
 * @internal
 */
export class EmptyFolderBase extends React.PureComponent<IEmptyFolderBaseProps> {
  public render(): JSX.Element | null {
    const {
      childItems,
      itemKey,
      onLayoutRenderComplete
    } = this.props;
    const isEmpty = !!childItems && !!childItems.itemKeys && childItems.itemKeys.length === 0;

    return isEmpty ? (
      <WithItemCache>
        {
          ({ itemCache, dispatch }: IItemCacheStateProps): JSX.Element | null => {
            const controlHandler = itemCache.demandItemFacet(emptyFolderControl, itemKey);
            const {
              emptyFolderIconUrl,
              emptyFolderTitle,
              getStyles
            } = emptyFolderControl.evaluate(controlHandler)(itemCache, {
              itemKey: itemKey
            });

            return <EmptyFolderComponentStyled
              { ...{
                emptyFolderIconUrl,
                emptyFolderTitle,
                onLayoutRenderComplete,
                styles: getStyles
              } }
            />;
          }
        }
      </WithItemCache>
    ) : null;
  }
}

/**
 * @public
 */
export type IEmptyFolderProps = Omit<IEmptyFolderBaseProps, 'childItems'> & {
  itemKey: string;
};

/**
 * @public
 */
export const EmptyFolder: React.ComponentType<IEmptyFolderProps> = connectToItemCache((
  itemCache: IItemCache,
  props: {
    itemKey: string;
  }
): Pick<IEmptyFolderBaseProps, 'childItems' | 'itemKey'> => {
  const {
    itemKey
  } = props;

  const childItems = itemCache.isItemFacetResolved(childItemsFacet, itemKey) ?
    itemCache.demandItemFacet(childItemsFacet, itemKey) :
    undefined;

  return {
    childItems: childItems,
    itemKey: itemKey
  };
})(EmptyFolderBase);
